/**
 * Unit conversion utilities for the component form.
 * Based on the measures table in migration 075.
 * Conversion factors represent: 1 <code> = <factor> <si_base_unit>
 */

export interface UsageUnit {
  code: string
  symbol: string
  name: string
  conversionFactor: number // to si_base_unit
  siBaseUnit: string
  isCulinary: boolean
}

// Static map of all known measures (mirrors DB migration 075)
// Grouped by SI base unit for easy compatible-units lookup
const ALL_UNITS: UsageUnit[] = [
  // Mass (base: kg)
  { code: 'kg',   symbol: 'kg',   name: 'Quilogramas',        conversionFactor: 1,        siBaseUnit: 'kg', isCulinary: false },
  { code: 'g',    symbol: 'g',    name: 'Gramas',             conversionFactor: 0.001,    siBaseUnit: 'kg', isCulinary: false },
  { code: 'mg',   symbol: 'mg',   name: 'Miligramas',         conversionFactor: 0.000001, siBaseUnit: 'kg', isCulinary: false },
  { code: 't',    symbol: 't',    name: 'Toneladas',           conversionFactor: 1000,     siBaseUnit: 'kg', isCulinary: false },
  { code: 'saca', symbol: 'saca', name: 'Sacas',              conversionFactor: 60,       siBaseUnit: 'kg', isCulinary: false },

  // Volume (base: L)
  { code: 'L',            symbol: 'L',       name: 'Litros',             conversionFactor: 1,       siBaseUnit: 'L', isCulinary: false },
  { code: 'ml',           symbol: 'ml',      name: 'Mililitros',         conversionFactor: 0.001,   siBaseUnit: 'L', isCulinary: false },
  { code: 'metro_cubico', symbol: 'm³',      name: 'Metros cúbicos',     conversionFactor: 1000,    siBaseUnit: 'L', isCulinary: false },
  // Culinary - volume
  { code: 'xicara',      symbol: 'xic',      name: 'Xícaras (240 ml)',          conversionFactor: 0.240,   siBaseUnit: 'L', isCulinary: true },
  { code: 'col_sopa',    symbol: 'col',      name: 'Colheres de sopa (15 ml)',  conversionFactor: 0.015,   siBaseUnit: 'L', isCulinary: true },
  { code: 'col_cha',     symbol: 'col chá',  name: 'Colheres de chá (5 ml)',    conversionFactor: 0.005,   siBaseUnit: 'L', isCulinary: true },
  { code: 'pinca',       symbol: 'pinça',    name: 'Pinças (~0,5 ml)',          conversionFactor: 0.0005,  siBaseUnit: 'L', isCulinary: true },
  { code: 'gota',        symbol: 'gota',     name: 'Gotas (~0,05 ml)',          conversionFactor: 0.00005, siBaseUnit: 'L', isCulinary: true },
  // Culinary - mass (aproximações típicas de receitas brasileiras / ração agro)
  // 1 xícara de ingrediente sólido ≈ 120 g (farinha, açúcar fino, granéis)
  { code: 'xicara_g',    symbol: 'xic',      name: 'Xícaras (~120 g)',          conversionFactor: 0.120,   siBaseUnit: 'kg', isCulinary: true },
  // 1 colher de sopa ≈ 15 g (sal, açúcar, farinha, suplemento)
  { code: 'col_sopa_g',  symbol: 'col',      name: 'Colheres de sopa (~15 g)',  conversionFactor: 0.015,   siBaseUnit: 'kg', isCulinary: true },
  // 1 colher de chá ≈ 5 g
  { code: 'col_cha_g',   symbol: 'col chá',  name: 'Colheres de chá (~5 g)',    conversionFactor: 0.005,   siBaseUnit: 'kg', isCulinary: true },
  // 1 pitada ≈ 0,3 g (sal, especiarias)
  { code: 'pitada',      symbol: 'pitada',   name: 'Pitadas (~0,3 g)',          conversionFactor: 0.0003,  siBaseUnit: 'kg', isCulinary: true },

  // Length (base: m)
  { code: 'm',  symbol: 'm',  name: 'Metros',        conversionFactor: 1,     siBaseUnit: 'm', isCulinary: false },
  { code: 'cm', symbol: 'cm', name: 'Centímetros',   conversionFactor: 0.01,  siBaseUnit: 'm', isCulinary: false },
  { code: 'mm', symbol: 'mm', name: 'Milímetros',    conversionFactor: 0.001, siBaseUnit: 'm', isCulinary: false },

  // Count (no SI base, no conversion)
  { code: 'un',      symbol: 'un',      name: 'Unidades',   conversionFactor: 1, siBaseUnit: 'un',      isCulinary: false },
  { code: 'duzia',   symbol: 'dz',      name: 'Dúzias',     conversionFactor: 12, siBaseUnit: 'un',     isCulinary: false },
  { code: 'milheiro',symbol: 'milh',    name: 'Milheiros',  conversionFactor: 1000, siBaseUnit: 'un',   isCulinary: false },

  // Packaging / containers (treated as "count" - no conversion)
  { code: 'rolo',    symbol: 'rolo',    name: 'Rolos',      conversionFactor: 1, siBaseUnit: 'rolo',    isCulinary: false },
  { code: 'novelo',  symbol: 'nov',     name: 'Novelos',    conversionFactor: 1, siBaseUnit: 'novelo',  isCulinary: false },
  { code: 'folha',   symbol: 'folha',   name: 'Folhas',     conversionFactor: 1, siBaseUnit: 'folha',   isCulinary: false },
  { code: 'cartela', symbol: 'cartela', name: 'Cartelas',   conversionFactor: 1, siBaseUnit: 'cartela', isCulinary: false },
  { code: 'pacote',  symbol: 'pct',     name: 'Pacotes',    conversionFactor: 1, siBaseUnit: 'pacote',  isCulinary: false },
  { code: 'caixa',   symbol: 'caixa',   name: 'Caixas',     conversionFactor: 1, siBaseUnit: 'caixa',   isCulinary: false },
  { code: 'lata',    symbol: 'lata',    name: 'Latas',      conversionFactor: 1, siBaseUnit: 'lata',    isCulinary: false },
  { code: 'pote',    symbol: 'pote',    name: 'Potes',      conversionFactor: 1, siBaseUnit: 'pote',    isCulinary: false },
  { code: 'garrafa', symbol: 'garr',    name: 'Garrafas',   conversionFactor: 1, siBaseUnit: 'garrafa', isCulinary: false },
  { code: 'frasco',  symbol: 'frasc',   name: 'Frascos',    conversionFactor: 1, siBaseUnit: 'frasco',  isCulinary: false },
  { code: 'dose',    symbol: 'dose',    name: 'Doses',      conversionFactor: 1, siBaseUnit: 'dose',    isCulinary: false },
]

const UNIT_MAP = new Map(ALL_UNITS.map((u) => [u.code, u]))

/**
 * Get the UsageUnit metadata for a given measure code.
 */
export function getUnit(code: string): UsageUnit | undefined {
  return UNIT_MAP.get(code)
}

/**
 * Returns usage units compatible with the given base unit code.
 * Includes all units that share the same SI base unit (i.e. convertible),
 * plus culinary units when applicable (volume / mass).
 * Falls back to just the base unit if unknown.
 */
export function getCompatibleUnits(baseCode: string): UsageUnit[] {
  const base = UNIT_MAP.get(baseCode)
  if (!base) {
    // Unknown unit: just offer itself
    return [{ code: baseCode, symbol: baseCode, name: baseCode, conversionFactor: 1, siBaseUnit: baseCode, isCulinary: false }]
  }

  return ALL_UNITS.filter(
    (u) => u.siBaseUnit === base.siBaseUnit
  )
}

/**
 * Convert a quantity from `fromCode` to `toBaseCode` (the component's unit).
 * Returns null if conversion is not possible (different categories).
 *
 * Example: convertToBase(5, 'col_sopa', 'L') → 0.075
 */
export function convertToBase(qty: number, fromCode: string, toBaseCode: string): number | null {
  if (fromCode === toBaseCode) return qty

  const from = UNIT_MAP.get(fromCode)
  const to = UNIT_MAP.get(toBaseCode)

  if (!from || !to) return null
  if (from.siBaseUnit !== to.siBaseUnit) return null

  // qty in fromCode → SI → toBaseCode
  const inSI = qty * from.conversionFactor
  return inSI / to.conversionFactor
}

/**
 * Format a converted quantity for display (removes trailing zeros, limits decimals).
 */
export function formatConverted(value: number): string {
  if (value === 0) return '0'
  if (value >= 1) return parseFloat(value.toPrecision(4)).toString()
  return parseFloat(value.toPrecision(3)).toString()
}
